<template>
  <div class="wrap">
    <DialogPanel modal-name="restore-photo-dialog"
                 main-button-text="復元"
                 @main-button-click="restoreSelectedPhotos()"
                 second-button-text="キャンセル"
                 @second-button-click="() => { $refs.theRestorePhotoDialog.hide() }"
                 ref="theRestorePhotoDialog">
      <div class="dialog-title">
        写真の復元
      </div>
      <div class="dialog-caption">
        選択した写真{{ restorePhotoKeyList.length }}枚を復元します。<br />
        復元を実行しますか？
      </div>
    </DialogPanel>
    <PhotoDownloadDialog ref="photoDownloadDialog" @change-img="changeImage($event)" :noDownload="true" />
    <div class="title">
      <router-link :to="{ name: 'create-kokuban-detail', params: { cloudConstructionId: $route.params.cloudConstructionId } }">
        {{ currentConstruction.cloud_construction_name }}
      </router-link>
      ＞削除された写真
    </div>
    <div class="search-wrap">
      <vue-simple-suggest v-model="searchKeyword"
                          :list="suggestWords"
                          v-show="searchMode === 'keyword'"
                          :styles="autoCompleteStyle"
                          :filter-by-query="true"
                          :destyled="true"
                          :min-length="0">
        <input type="text" class="input-search-keyword" placeholder="キーワードを入力">
      </vue-simple-suggest>
      <div v-show="searchMode === 'date'"
           class="search-shooting-date-wrap">
        <span>撮影日</span>
        <ValidationProvider rules="slash_separated_date" v-slot="{ errors }" ref="shootingDateInput">
          <input type="text"
                 class="input-search-shooting-date"
                 v-model="searchShootingDate">
          <span v-if="errors[0]" class="shooting-date-format-error">{{errors[0]}}</span>
        </ValidationProvider>
      </div>
      <select v-model="searchMode"
              class="search-mode-select">
        <option v-for="searchMode in searchModeList"
                :key="searchMode.value"
                :value="searchMode.value"
                :disabled="searchMode.disabled === true">
          {{ searchMode.text }}
        </option>
      </select>
      <button class="search-button" @click="doSearch">
        検索
      </button>
      <button class="restore-button"
              :disabled="restorePhotoKeyList.length < 1"
              @click="confirmRestorePhotos">
        写真を復元
      </button>
      <div v-if="searchExecuted">
        <button class="close-search-result-button" @click="resetSearchMode">
          <span>検索結果: {{ addComma(searchResultCount) }} 件 &#x2715;</span>
        </button>
      </div>
    </div>
    <div class="imagelist-wrap">
      <div class="date-wrap" v-for="(ymd) in Object.keys(dateGroupedImgs).sort().reverse()" :key="ymd">
        <div class="date">{{format8charDate(ymd)}}</div>
        <div v-for="photo in dateGroupedImgs[ymd]" :key="photo.photo_key" class="photo-flex">
          <div class="send-checkbox-wrap">
            <label>
              <input type="checkbox"
                     class="send-checkbox"
                     v-model="photo.checked"
                     :id="'photo_checkbox_'+photo.photo_key">
              <span class="checkbox-parts"
                    :class="{ checked: photo.checked }"></span>
            </label>
          </div>
          <div class="photo-wrap">
            <a key="loaded"
               :class="photo.active_link_target"
               @click.prevent="openDownloadDialog(photo)"
               @keyup.enter="openDownloadDialog(photo)"
               tabindex="0">
              <img :id="photo.photo_key"
                    class="photo"
                    :src="photo.thumbnail_s_url"
                    :ref="photo.photo_key">
              <div class="photo-by">
                <span>撮影者</span>
                <span>{{photo.kuraemon_cloud_user_name}}</span>
              </div>
            </a>
          </div>
          <div class="info-wrap">
            <div v-for="(kokubanInfo, i) in kokubanParam(photo.kokuban_info_json)" :key="kokubanInfo.index" class="kokuban-info">
              <span v-if="i < maxKokubanLineCount">
                {{ kokubanInfo.title }}:{{ kokubanInfo.value }}
              </span>
              <span v-if="i === maxKokubanLineCount">⋮</span>
            </div>
          </div>
        </div>
      </div>
      <infinite-loading spinner="circles" @infinite="handleInfinite" ref="infiniteLoading" >
        <div slot="no-more">これ以上結果がありません</div>
        <div slot="no-results">結果がありません</div>
      </infinite-loading>
      <div v-if="searchExecuted && displaySearchResultCount < searchResultCount" class="search-result-warning">
        {{ addComma(displaySearchResultCount) }} 件までしか表示していません
      </div>
    </div>
  </div>
</template>

<script>
/**
 * @typedef {{
 * photo_key: string,
 * shooting_ymd: string,
 * kuraemon_cloud_user_name: string,
 * uploaded_at: number?,
 * deleted_at: number?,
 * extension: string,
 * kokuban_info_json: string,
 * original_url: string?,
 * thumbnail_s_url: string?,
 * thumbnail_l_url: string?,
 * checked: boolean?,
 * }} PhotoRec
 */

import InfiniteLoading from 'vue-infinite-loading';
import moment from 'moment'

// ie対応のためこの形式で読み込んでいる
const VueSimpleSuggest = require('vue-simple-suggest/dist/cjs')

import smoothscroll from 'smoothscroll-polyfill';
smoothscroll.polyfill();

import { ValidationProvider, extend, configure } from 'vee-validate'
import * as rules from 'vee-validate/dist/rules'
import ja from 'vee-validate/dist/locale/ja.json'

// デフォルトのルールとエラーメッセージを読み込む
Object.keys(rules).forEach(rule => {
  extend(rule, {
    ...rules[rule],
    message: ja.messages[rule]
  });
});
// エラー時のclassとか
configure({
  classes: {
    valid: 'is-valid',
    invalid: 'is-invalid'
  }
});
// スラッシュ区切りの日付のフォーマットではない
extend('slash_separated_date', value => {
  const pattern = /^[0-9]{4}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/;
  if (value.match(pattern)) {
    return true;
  }
  return '日付はYYYY/MM/DDの形式で入力してください';
})

import IconUtils from '@/utils/icon_utils.js'

// photo_key 降順でソートするルール
const sortPhotoKeyDesc = (a, b) => {
  const aKey = a.photo_key;
  const bKey = b.photo_key;
  if(aKey === bKey) return 0;
  if(aKey < bKey) return 1;
  return -1;
}

export default {
  name: 'DeletedPhoto',
  components: {
    InfiniteLoading,
    PhotoDownloadDialog: () => import("@/components/ui/PhotoDownloadDialog"),
    DialogPanel: () => import('@/components/ui/DialogPanel.vue'),
    ValidationProvider,
    VueSimpleSuggest,
  },
  props: {
    currentConstruction: Object,
    suggestWords: Array
  },
  data() {
    return {
      autoCompleteStyle: {
        vueSimpleSuggest: "suggest-wrap",
        inputWrapper: "",
        defaultInput: "input-search-keyword",
        suggestions: "suggest-item-wrap",
        suggestItem: "suggest-item"
      },
      searchKeyword: "",
      searchShootingDate: "",
      /**
       * APIから取得した結果をobjectにしてためていく
       * @type {{ [photo_key: string]: PhotoRec }},
       */
      photos: {},
      /** 表示対象とするphoto_key (ソートなし) */
      displayPhotoKeys: [],
      /**
       * 次回検索するときの先頭のひとつ手前のPhoto-key 
       * @type {string | null}
       */
      exclusiveStartPhotoKey: null,
      /** 検索が実行されている状態かどうか */
      searchExecuted: false,
      /** 検索モード選択肢 */
      searchModeList: [
        { value: "", text: "選択してください", disabled: true },
        { value: "keyword", text: "キーワード検索" },
        { value: "date", text: "撮影日検索" }
      ],
      /**
       * 検索モード
       * @type {"keyword" | "date"}
       */
      searchMode: "keyword",
      /** 検索結果（総件数：表示されないものも含む件数） */
      searchResultCount: 0,
    }
  },
  computed: {
    /**
     * 画面表示用に、日付でカテゴリ化したリスト
     */
    dateGroupedImgs(){
      /** @type {{[shootingYmd: string]: PhotoRec[]}}  */
      const dateGrouped = {};
      for(const photoKey of this.displayPhotoKeys){
        const rec = this.photos[photoKey];
        const shootingYmd = rec.shooting_ymd;
        if(!dateGrouped[shootingYmd]) dateGrouped[shootingYmd] = [];
        dateGrouped[shootingYmd].push(rec)
      }
      Object.entries(dateGrouped).forEach(([, recs]) => {
        recs.sort(sortPhotoKeyDesc)
      })
      return dateGrouped;
    },
    /** photo_key 降順ソートしたphoto_keyの配列を返す */
    sortedPhotoKeys(){
      return Object.entries(this.photos).sort(
        (a, b) => (sortPhotoKeyDesc(a[1], b[1]))
      ).map(rec => rec[0]);
    },
    /** ダウンロードのphoto_key並び順 */
    downloadPhotoKeys(){
      const dates = Object.keys(this.dateGroupedImgs).sort().reverse();
      const nestedPhotoKeys = dates.map((ymd) => this.dateGroupedImgs[ymd].map(rec => rec.photo_key));
      return [].concat(...nestedPhotoKeys);
    },
    /**
     * 検索パラメータを返す
     * @returns {{search_string: string} | {shooting_ymd: {from: string, to: string}} | null}
     */
    searchParams(){
      if(this.searchMode === 'keyword'){
        if(!this.searchKeyword){
          return null;
        }
        return { search_string: this.searchKeyword };
      }
      if(this.searchMode === 'date'){
        if(!this.searchShootingDate){
          return null;
        }
        const shootingYmd = this.formatToYmd(this.searchShootingDate)
        return {
          shooting_ymd: {
            from: shootingYmd,
            to: shootingYmd,
          }
        }
      }
      return null;
    },
    /** 検索結果の表示中件数 */
    displaySearchResultCount(){
      if(!this.searchExecuted){
        return 0;
      }
      return this.displayPhotoKeys.length;
    },
    /** 黒板表示最大行数 */
    maxKokubanLineCount(){
      return 7;
    },
    /** 復元対象のキーリスト */
    restorePhotoKeyList() {
      // チェックが付いた写真のphoto_keyを配列にする
      const checkedPhotos = Object.values(this.photos).filter(photo => photo.checked);
      const checkedPhotoKeys = checkedPhotos.map(photo => photo.photo_key);
      return checkedPhotoKeys.filter(photoKey => this.displayPhotoKeys.includes(photoKey));
    }
  },
  methods: {
    /**
     * sortedPhotoKeysから、該当するsearchTargetPhotoKeyのインデックスを返す
     * @param {string} searchTargetPhotoKey 検索対象のphotoKey
     * @returns {number} sortedPhotoKeysから、該当するsearchTargetPhotoKeyのインデックス
     */
    findPhotoKeyIndex(searchTargetPhotoKey){
      let index = 0;
      for(const photoKey of this.sortedPhotoKeys){
        if(photoKey <= searchTargetPhotoKey){
          // 同じもしくは、超えたところのインデックスを返す
          return index;
        }
        ++index;
      }
      // 超えるところがない場合は、最後までの範囲を返す
      return index;
    },
    // 取得した範囲になくなった画像を photos から削除する
    deletePhotos(newPhotoList, startIndex, exclusiveLastIndex){
      const deletedPhotoEntries = Object.entries(this.sortedPhotoKeys).filter(([index, photoKey]) => {
        if(startIndex <= index && index < exclusiveLastIndex){
          if( ! newPhotoList.find(photo => photo.photo_key === photoKey) ){
            // 取得したデータに見つからない場合
            return true; // 削除されている
          }
        }
        return false;
      })

      deletedPhotoEntries.forEach(([, photoKey]) => {
        this.$delete(this.photos, photoKey) // 削除
      })
    },
    // 写真が更新されているかどうかをチェックする
    isUpdatedPhoto(photo){
      const curPhoto = this.photos[photo.photo_key];
      if(!curPhoto) return true;
      if(!curPhoto.thumbnail_s_url) return true;
      if(curPhoto.extension !== photo.extension) return true; // extension updated
      if(curPhoto.uploaded_at !== photo.uploaded_at) return true; // uploaded_at updated
      return false; // no update
    },
    // 内容が更新された写真リストの情報を上書きする
    async updateRevisedPhotoContent(newPhotoList){
      // 内容が更新された写真リスト
      const revisedPhotoList = newPhotoList.filter(this.isUpdatedPhoto)

      // 更新された写真リストについてurlを取得する
      const photoIds = revisedPhotoList.map(
        ({photo_key, extension}) => ({photo_key, extension, image_type: 'thumbnail_s'})
      );
      const photoUrls = await this.getPhotoUrls(photoIds)

      // 内容が更新された写真リストのURLを更新する
      revisedPhotoList.forEach((photo) => {
        photo.thumbnail_s_url = photoUrls[photo.photo_key]
        this.$set(this.photos, photo.photo_key, photo);
      });
    },
    // 最新の写真をロードする
    async loadRecentPhotos(oldestPhotoKey){
      if(this.searchExecuted) return;
      if(!oldestPhotoKey){
        // 最過去のphoto_key指定がない場合、スクロールモードで100件取りに行く
        // console.log("scroll mode");
        this.loadScrolledPhotos(null)
        return;
      }
      const args = {
        cloud_construction_id: this.$route.params.cloudConstructionId,
        photo_key: {},
        recent: {
          oldest_photo_key: oldestPhotoKey
        },
        is_deleted_only: true,
        __apiOptions: { noDisplayLoading: true }
      };
      const photoList = []
      for(;;){
        const result = await this.$store.dispatch('api/getPhotoData', args);
        if (this.$store.getters['api/apiError']) {
          return;
        }
        if(result.photoList && 0 < result.photoList.length){
          result.photoList.forEach(photo => photoList.push(photo))
        }
        if(result.recent && result.recent.last_evaluated_photo_key){
          args.recent.last_evaluated_photo_key = result.recent.last_evaluated_photo_key
          continue;
        }
        break;
      }
      // console.log("photoList.length", photoList.length);

      // 内容が更新された写真リストの情報を上書きする
      await this.updateRevisedPhotoContent(photoList);

      { // 取得した範囲になくなった画像を photos から削除する
        // 取得結果のphote_key
        const lastKey = (() => {
          if(photoList.length < 1){
            // 取得結果がない場合は、photo_key = null として削除範囲を探す
            return null;
          }
          return photoList[photoList.length - 1].photo_key
        })()

        // sortedPhotoKeys を探す範囲
        const startIndex = 0
        const exclusiveLastIndex = this.findPhotoKeyIndex(lastKey)
        this.deletePhotos(photoList, startIndex, exclusiveLastIndex)
      }

      // 今回取得した最も古いPhotoKey
      const curOldestPhotoKey = photoList.map(photo => photo.photo_key).sort()[0]

      // 表示対象とする photo_key を今回取得済みの範囲に絞る
      this.displayPhotoKeys = this.sortedPhotoKeys.filter(
        photoKey => (curOldestPhotoKey <= photoKey)
      );
    },
    // スクロール時に写真をロードする
    async loadScrolledPhotos(exclusiveStartPhotoKey){
      if(this.searchExecuted) return;
      const args = {
        cloud_construction_id: this.$route.params.cloudConstructionId,
        photo_key: {
          from: exclusiveStartPhotoKey,
          to: null,
        },
        is_deleted_only: true,
        __apiOptions: { noDisplayLoading: true }
      };
      const { photoList } = await this.$store.dispatch('api/getPhotoData', args);
      if (this.$store.getters['api/apiError']) {
        return {};
      }

      if(photoList.length < 1){
        // 取得結果がない。全部取得完了
        return { complete: true };
      }

      // 内容が更新された写真リストの情報を上書きする
      await this.updateRevisedPhotoContent(photoList);

      { // 取得した範囲になくなった画像を photos から削除する
        // 取得結果のphote_key
        const firstKey = photoList[0].photo_key
        const lastKey = photoList[photoList.length - 1].photo_key

        // sortedPhotoKeys を探す範囲
        const startIndex = this.findPhotoKeyIndex(firstKey) // 途中から取得した場合
        const exclusiveLastIndex = this.findPhotoKeyIndex(lastKey)
        this.deletePhotos(photoList, startIndex, exclusiveLastIndex)
      }

      // 次回取得時の開始キー
      const oldestPhotoKey = photoList.map(photo => photo.photo_key).sort()[0]

      // 表示対象とする photo_key を今回取得済みの範囲に絞る
      this.displayPhotoKeys = this.sortedPhotoKeys.filter(
        photoKey => (oldestPhotoKey <= photoKey)
      );

      // 次回取得時の開始キー更新
      this.exclusiveStartPhotoKey = oldestPhotoKey

      return { loaded: true };
    },
    // 写真のURL(複数)を取得する
    async getPhotoUrls(photoIds){
      const args = {
        cloud_construction_id: this.$route.params.cloudConstructionId,
        photo_ids: photoIds,
        return_type: 'object',
        __apiOptions: { noDisplayLoading: true }
      }
      const photoUrls = await this.$store.dispatch('aws/photoUrlsOf', args);
      return photoUrls;
    },
    // 検索実施
    async doSearch(apiOptions = { noDisplayLoading: false }){
      if (this.$refs.shootingDateInput && !this.$refs.shootingDateInput.flags.valid) {
        alert('撮影日がYYYY/MM/DDの形式になっているか確認してください');
        return;
      }
      if(!this.searchParams){
        alert('検索条件が設定されていません');
        return;
      }

      const args = {
        cloud_construction_id: this.$route.params.cloudConstructionId,
        photo_key: {},
        ...this.searchParams,
        is_deleted_only: true,
        __apiOptions: { ...apiOptions }
      };

      this.searchExecuted = true;
      this.searchResultCount = 0;
      this.$refs.infiniteLoading.stateChanger.reset(); // スクロールローディングの表示リセット
      const { photoList, resultCount } = await this.$store.dispatch('api/getPhotoData', args);
      if (this.$store.getters['api/apiError']) {
        this.searchExecuted = false;
        return;
      }
      this.searchResultCount = resultCount;
      if(0 < photoList.length){
        // 内容が更新された写真のリスト
        const revisedPhotoList = photoList.filter(this.isUpdatedPhoto)

        // 更新された写真リストについてurlを取得する
        const photoIds = revisedPhotoList.map(
          ({photo_key, extension}) => ({photo_key, extension, image_type: 'thumbnail_s'})
        );
        const photoUrls = await this.getPhotoUrls(photoIds)

        // 内容が更新された写真リストの情報を上書きする
        revisedPhotoList.forEach((photo) => {
          photo.thumbnail_s_url = photoUrls[photo.photo_key]
          this.$set(this.photos, photo.photo_key, photo);
        });
      }
      // 検索ヒットした photo_key のみ、表示対象にする
      this.displayPhotoKeys = photoList.map(photo => photo.photo_key);
    },
    // 検索モードクリアして通常モードのデータをロードする
    resetSearchMode(){
      this.displayPhotoKeys = []; // 表示対象 photo_key をクリア
      this.searchExecuted = false;
      this.loadRecentPhotos(this.exclusiveStartPhotoKey); // 再ロード
    },
    // 最下段スクロールで、データをロードする
    handleInfinite($state){
      if(this.searchExecuted){
        $state.reset();
        return;
      }
      this.loadScrolledPhotos(this.exclusiveStartPhotoKey).then(({ complete, loaded }) => {
        if(complete){
          $state.complete();
          return;
        }
        if(loaded){
          $state.loaded();
          return;
        }
        $state.reset();
      })
    },
    // 前後画像のキーをセットする
    applyNavigationPhoteKeys(photo){
      const currentIdx = this.downloadPhotoKeys.indexOf(photo.photo_key);

      const prevPhotoKey = (0 < currentIdx ? this.downloadPhotoKeys[currentIdx - 1] : null);
      const prevFrameKey = (prevPhotoKey ? (this.photos[prevPhotoKey].uploaded_at ? prevPhotoKey : null) : null);

      const nextPhotoKey = (currentIdx < this.downloadPhotoKeys.length - 1 ? this.downloadPhotoKeys[currentIdx + 1] : null);
      const nextFrameKey = (nextPhotoKey ? (this.photos[nextPhotoKey].uploaded_at ? nextPhotoKey : null) : null);

      this.$set(photo, 'prevFrameKey', prevFrameKey);
      this.$set(photo, 'nextFrameKey', nextFrameKey);
    },
    // 写真ダウンロードダイアログを開く
    openDownloadDialog(photo) {
      this.applyNavigationPhoteKeys(photo);
      // ダイアログを開く
      this.$refs.photoDownloadDialog.show(photo);
    },
    // 写真ダウンロードダイアログの画像を切り替える
    changeImage(targetFrameKey) {
      // 切替対象の画像を取得
      const photo = this.photos[targetFrameKey];
      this.applyNavigationPhoteKeys(photo);
      // ダイアログ側の画像データを入れ替える
      this.$refs.photoDownloadDialog.changeTo(photo);
    },
    nameToColor(name) {
      return IconUtils.nameToColor(name);
    },
    formatTimestamp(timestamp) {
      return moment.unix(timestamp).format('Y/M/D H:mm:ss')
    },
    format8charDate(dateStr) {
      return moment(dateStr, "YYYYMMDD").format('YYYY/MM/DD')
    },
    formatToYmd(dateStr) {
      return moment(dateStr, "YYYY/MM/DD").format('YYYYMMDD')
    },
    // 数値に3桁区切りでカンマをつける
    addComma(num) {
      return String(num).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
    },
    kokubanParam(kokubanInfoJson) {
      try{
        const kokubanInfoObj = JSON.parse(kokubanInfoJson);
        if (kokubanInfoObj.kokuban_param) {
          return kokubanInfoObj.kokuban_param
            .filter(elem => (elem.title))
            .sort((a, b) => (a.index - b.index))
            ;
        }
        return []
      }catch(e){
        return []
      }
    },
    // 写真復元ボタン(確認ダイアログ表示)
    confirmRestorePhotos() {
      if (0 < this.restorePhotoKeyList.length) {
        this.$refs.theRestorePhotoDialog.show();
      }
    },
    // 選択した写真を復元
    async restoreSelectedPhotos() {
      const args = {
        cloud_construction_id: this.$route.params.cloudConstructionId,
        photo_key_list: this.restorePhotoKeyList
      };
      const errorPhotoKeys = await this.$store.dispatch('api/unDeletePhotoList', args);
      if (this.$store.getters['api/apiError']) {
        this.$refs.theRestorePhotoDialog.hide();
        return;
      }
      if (errorPhotoKeys && 0 < errorPhotoKeys.length ){
        const description = `${errorPhotoKeys.length}件の写真復元に失敗しました`
        console.log("errorPhotoKeys", errorPhotoKeys);
        this.$store.commit('api/setApiError', { description })
        this.$refs.theDeletePhotoDialog.hide();
        return;
      }

      await this.$store.dispatch("view/start"); // ローディング
      if (!this.searchExecuted) {
        // 先頭からのデータを再ロード
        console.log("**** reload after restored")
        await this.loadRecentPhotos(this.exclusiveStartPhotoKey)
      } else {
        // 再度 検索
        console.log("**** search after restored")
        await this.doSearch({ noDisplayLoading: true })
      }
      this.$refs.theRestorePhotoDialog.hide();
      await this.$store.dispatch("view/end"); // ローディング
    }
  },
}
</script>

<style>
/* vue-simple-suggestのサジェスト部分css */
.search-wrap .suggest-item-wrap {
  background-color: #FFF;
  border: solid 1px #333;
  list-style: none;
  margin: 0;
  opacity: 1;
  padding: 0;
  position: absolute;
  top: 100%;
  top: calc(100% + 5px);
  width: 100%;
  z-index: 1000;
}
.search-wrap .suggest-item {
  cursor: pointer;
  padding: 5px 10px;
}
.search-wrap .suggest-item.hover {
  background-color: #999 !important;
  color: #FFF !important;
}
.search-wrap .suggest-item.selected {
  background-color: #666;
  color: #FFF;
}
</style>
<style scoped>
.wrap {
  padding: 3rem;
}
.title {
  border-bottom: solid 1px #333;
  font-size: 1.4rem;
  font-weight: bold;
  padding: 2rem 1rem;
}
.title a {
  color: #333;
  cursor: pointer;
  text-decoration: none;
}
.title a:hover {
  opacity: 0.8;
}
/* 検索欄 */
.search-wrap {
  padding: 2rem 1rem 1rem 1rem;
}
.suggest-wrap {
  display: inline-block;
  position: relative;
}
.input-search-keyword {
  border: solid 1px #333;
  height: 35px;
  outline: none;
  padding: 0.5rem 1rem;
  width: 300px;
  vertical-align: middle;
}
.search-shooting-date-wrap {
  border: solid 1px #333;
  display: inline-block;
  height: 35px;
  padding: 0.5rem 1rem;
  position: relative;
  vertical-align: middle;
}
.input-search-shooting-date {
  border: none;
  margin-left: 1rem;
  outline: none;
  width: 100px;
}
.search-mode-select {
  border: solid 1px #333;
  display: inline-block;
  height: 35px;
  margin-left: -1px;
  outline: none;
  padding: 0.5rem 1rem;
  vertical-align: middle;
}
.search-button {
  background-color: #666;
  border: none;
  color: white;
  cursor: pointer;
  height: 35px;
  margin-left: 1rem;
  padding: 0.5rem 1rem;
  width: 80px;
  vertical-align: middle;
}
.restore-button {
  background-color: #666;
  border: none;
  color: white;
  cursor: pointer;
  height: 35px;
  margin-left: 1rem;
  padding: 0.5rem 1rem;
  width: 130px;
  vertical-align: middle;
}
.restore-button:disabled {
  background-color: #999;
  color: #ccc;
}
.shooting-date-format-error {
  position: absolute;
  background-color: #333;
  color: white;
  top: 3rem;
  left: 0%;
  width: 200px;
  padding: 0.5rem;
}
.shooting-date-format-error::before {
  content: "";
  border-bottom: 1rem solid #333;
  border-right: 0.5rem solid transparent;
  border-left: 0.5rem solid transparent;
  position: absolute;
  top: calc(0% - 1rem);
  left: 50%;
}
.close-search-result-button {
  border: solid 1px #999;
  border-radius: 1.5rem;
  background-color: #EEE;
  color: #333;
  cursor: pointer;
  margin: 1rem 0;
  padding: 0.5rem 1rem;
}
.search-result-warning {
  text-align: center;
}

.date-wrap {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}
.date-wrap .date {
  font-size: 1.4rem;
  padding: 1rem 2rem;
  width: 100%;
}
.photo-flex {
  width: calc(50% - 10px);
  height: auto;
  position: relative;
  margin: 5px 5px 5rem 5px;
}
.photo-flex::before {
  content: '';
  display: block;
  padding-top: 50%;
}
/* チェックボックス */
.send-checkbox-wrap {
  display: inline-block;
  position: absolute;
  height: 2rem;
  width: 2rem;
  padding: 0.25rem;
  top: 0;
}
.send-checkbox {
  filter: alpha(opacity=0);
  -moz-opacity: 0;
  opacity: 0;
  -webkit-appearance: none;
  appearance: none;
  position: absolute;
  z-index: 1;
}
.checkbox-parts {
  position:absolute;
  top: 0.25rem;
  left: 0.25rem;
  margin-bottom: 0.25rem;
}
.checkbox-parts::before {
  content: "";
  display: block;
  width: 1.5rem;
  height: 1.5rem;
  border: 1px solid #666;
  border-radius: 3px;
  background: #eee;
}
.send-checkbox:focus + .checkbox-parts::before {
  box-shadow: 0 0 0.4rem #333;
}
.checkbox-parts.checked::after {
  content: "";
  display: block;
  position: absolute;
  top: -5px;
  left: 4px;
  width: 1rem;
  height: 1.5rem;
  transform: rotate(40deg);
  border-bottom: 3px solid #555;
  border-right: 3px solid #555;
}
@media screen and (max-width: 767px) {
  .photo-flex {
    width: calc(100% - 10px);
  }
}
.photo-wrap {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 100%;
  margin-top: 2rem;
}
.photo-wrap > div,
.photo-wrap > a {
  width: 100%;
  height: 100%;
  position: relative;
  text-align: center;
}
.photo-wrap > a {
  display: block;
  cursor: pointer;
}
.photo{
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
}
.photo-by {
  display: inline-block;
  text-align: center;
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  background-color: rgba(255,255,255,0.8);
  padding: 5px 0;
}
.photo-by span:nth-of-type(1) {
  margin-right: 1rem;
}

.photo-by .new-photo {
  color: red;
}

.loading-wrap {
  width: 100%;
  height: 100%;
}
.spinner {
  width: 100%;
  height: 100%;
}
.spinner img {
  width: 100%;
  margin: auto;
}

.info-wrap {
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  width: 50%;
  height: 100%; 
  padding: 10px;
  margin-top: 2rem;
}
.kokuban-info {
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
/** ダイアログパネル */
.dialog-title {
  font-size: 1.2rem;
  font-weight: bold;
  margin: 0.5rem 0;
}
@keyframes showFirst {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@keyframes show {
  0% {
    opacity: 0;
    -moz-transform: translateY(0); /* firefoxはcollapseと一緒に下がってくれなかった */
  }
  50% {
    opacity: 0;
    -moz-transform: translateY(262px);
  }
  100% {
    opacity: 1;
    -moz-transform: translateY(262px);
  }
}
@keyframes hide {
  0% {
    opacity: 1;
  }
  0% {
    opacity: 0;
  }
}
</style>